<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <!-- PRODUCTS TABLE -->
        <b-card
          no-body
          class="border mt-1"
        >
          <b-card-header class="p-1">
            <b-card-title class="font-medium-2">
              <feather-icon
                icon="FileTextIcon"
                size="18"
              />
              <span class="align-middle ml-50">{{ $t('DEBTIA_INVOICES') }}</span>
            </b-card-title>
          </b-card-header>
          <b-form-group>
            <div class="table-responsive">
              <b-table
                v-if="!loader"
                ref="roles"
                class="position-relative"
                :fields="invoiceFields"
                :items="invoiceData"
              >
                <template
                  #cell(invoice_date)="data"
                >
                  {{ formattedDate(data.item.invoice_date) }}
                </template>

                <template
                  #cell(total)="data"
                >
                  {{ formatCurrency(data.item.total) }}
                </template>
                <template
                  #cell(due_date)="data"
                >
                  {{ formattedDate(data.item.due_date) }}
                </template>
                <template #cell(file_link)="data">
                  <feather-icon
                    icon="ExternalLinkIcon"
                    @click="openLink(data.item.file_link)"
                  />
                </template>
              </b-table>
            </div>
          </b-form-group>
        </b-card>

        <!-- PRODUCTS TABLE -->
        <b-card
          no-body
          class="border mt-1"
        >
          <b-card-header class="p-1">
            <b-card-title class="font-medium-2">
              <feather-icon
                icon="BoxIcon"
                size="18"
              />
              <span class="align-middle ml-50">{{ $t('DEBTIA_PRODUCTS') }}</span>
            </b-card-title>
          </b-card-header>
          <b-form-group>
            <b-table
              v-if="!loader"
              ref="roles"
              class="position-relative"
              :fields="invoiceProductsFields"
              :items="invoiceProductsData"
            />
          </b-form-group>

          <hr>
          <b-form-group class="p-2">
            <div
              class="col-6"
              style="float: right"
            >
              <div class="left">
                <p>
                  <small>{{ $t('DEBTIA_SUBTOTAL') }}</small>
                  <br>
                  <small>VAT (25%)</small>
                  <br>
                  <small>{{ $t('DEBTIA_TOTAL') }}</small>
                </p>
              </div>
              <div class="right">
                <p>
                  <small>{{ formatCurrency(subTotal) }}</small>
                  <br>
                  <small>{{ formatCurrency(vat) }}</small>
                  <br>
                  <small>{{ formatCurrency(total) }}</small>
                </p>
              </div>
            </div>
          </b-form-group>
        </b-card>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BFormGroup, BTable, BCard, BCardHeader, BCardTitle, BOverlay,
} from 'bootstrap-vue'
import router from '@/router'
import constants from '@/app-configs/constants'
import axios from '@axios'
import i18n from '@/libs/i18n'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BFormGroup,
    BTable,
    BCard,
    BCardHeader,
    BCardTitle,
    BOverlay,
  },
  data() {
    return {
      invoiceId: 0,
      invoiceData: [],
      invoiceProductsData: [],
      subTotal: 0.00,
      vat: 0,
      total: 0.00,
      loader: true,
      defaultCurrency: localStorage.getItem('defaultCurrency') || 'DKK',
    }
  },
  computed: {
    invoiceFields() {
      const temp = [
        { key: 'file_link', label: i18n.t('DEBTIA_FILE_LINK') },
        { key: 'client_bc_id', label: i18n.t('DEBTIA_CLIENT_BC_ID') },
        { key: 'client_name', sortable: true, label: i18n.t('DEBTIA_CLIENT_NAME') },
        { key: 'invoice_bc_id', label: i18n.t('DEBTIA_INVOICE_BC_ID') },
        { key: 'invoice_date', label: i18n.t('DEBTIA_INVOICE_DATE') },
        { key: 'due_date', label: i18n.t('DEBTIA_DUE_DATE') },
        { key: 'total', label: i18n.t('DEBTIA_TOTAL') },
      ]
      return temp
    },
    invoiceProductsFields() {
      const temp = [
        { key: 'no', label: i18n.t('DEBTIA_PRODUCT'), thStyle: { width: '300px' } },
        { key: 'quantity', label: i18n.t('DEBTIA_QUANTITY') },
        { key: 'unit_price', label: i18n.t('DEBTIA_UNIT_PRICE') },
        { key: 'discount', label: `${i18n.t('DEBTIA_DISCOUNT')} %` },
        { key: 'total', label: i18n.t('DEBTIA_TOTAL') },
      ]
      return temp
    },
  },
  async created() {
    await this.getPaymentsApiSettings()
  },
  methods: {
    async getPaymentsApiSettings() {
      await axios.get('/settings/get?module=payments').then(settings => {
        if (settings.status) {
          this.payment_client_id = settings.data.data.client_id
          this.payment_secret = settings.data.data.secret
          this.getInvoiceDetails()
        }
      }).catch(err => {
        console.log(err)
      })
    },
    async openLink(href) {
      window.open(href, '_blank')
    },
    formattedDate(date) {
      if (!date) return 'Invalid date' // Return an empty string if date is not provided
      // Convert the date to a string if it's not already one
      const dateString = String(date)
      // Check if the date string is in the correct format (YYYY-MM-DD)
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-')
        return `${day}-${month}-${year}`
      }
      // Handle invalid date format or other cases
      return 'Invalid date'
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('da-DK', { style: 'currency', currency: this.defaultCurrency }).format(parseFloat(amount).toFixed(2))
    },
    async getInvoiceDetails() {
      try {
        this.invoiceId = router.currentRoute.params.id
        this.loader = true
        const headers = {
          clientId: this.payment_client_id,
          secret: this.payment_secret,
        }
        const invoiceDetails = await axios.get(`${constants.paymentsBaseUrl}/external/invoice-system/invoice/details/${this.invoiceId}`, {
          headers,
        }).catch(error => {
          if (error.response.status === 404) {
            this.$router.push('/clients/list')
          }
        })
        this.invoiceData = [invoiceDetails.data.data]
        this.subTotal = invoiceDetails.data.data.sub_total
        this.vat = invoiceDetails.data.data.vat
        this.total = invoiceDetails.data.data.total
        this.invoiceProductsData = invoiceDetails.data.data.invoice_products.map(e => ({
          ...e,
          unit_price: this.formatCurrency(e.unit_price),
          discount: new Intl.NumberFormat('da-DK').format(parseFloat(e.discount).toFixed(2)),
          total: this.formatCurrency(e.total),
        }))
        this.loader = false
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Clients',
            icon: 'CoffeeIcon',
            variant: 'danger',
            text: e.message,
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
.spinner-border{
  width: 6rem;
  height: 6rem;
}
.left{
  float: left;
}
.right{
  float: right;
}
</style>
